import api from "./api";
import { SEARCH_ATTRIBUTES } from "../components/AttributeSearchInput";

const BASE_URL = "/fulfilment/accounts-payable/api/v1/vip";
const VALID_SEARCH_KEYS = new Set(SEARCH_ATTRIBUTES.map((a) => a.key));

export function getOrderList(data) {
  const {
    limit = 10,
    offset = 0,
    search = "",
    searchBy = "",
    filter = {},
    orderStatus,
    factoryCode = "",
  } = data || {};

  const { po_status: rawPoStatusFilter, ...restFilter } = filter;
  const poStatusFilter = Array.isArray(rawPoStatusFilter)
    ? rawPoStatusFilter
    : [];

  const searchParams = new URLSearchParams();
  searchParams.set("limit", limit);
  searchParams.set("offset", offset);
  searchParams.set("factory_code", factoryCode);

  const filterStatusValues = new Set();
  if (orderStatus && orderStatus !== "all_orders") {
    filterStatusValues.add(orderStatus);
  }
  poStatusFilter.forEach((v) => filterStatusValues.add(v));
  if (filterStatusValues.size) {
    searchParams.set("filter_status", Array.from(filterStatusValues).join(","));
  }

  if (search && searchBy && VALID_SEARCH_KEYS.has(searchBy)) {
    searchParams.set(searchBy, search);
  }

  const filterQueryString = Object.keys(restFilter).length
    ? new URLSearchParams(restFilter).toString()
    : "";
  const filterStr = filterQueryString ? `&${filterQueryString}` : "";

  return api.get(
    `${BASE_URL}/po-details?${searchParams.toString()}${filterStr}`
  );
}

// Maps frontend search attribute keys to invoice API param names
const INVOICE_SEARCH_KEY_MAP = {
  po_code: "po_code",
  vendor_name: "search",
  invoice_number: "invoice_no",
};

export function getInvoiceList(data) {
  const {
    limit = 10,
    offset = 0,
    search = "",
    searchBy = "",
    filter = {},
    status,
    factoryCode = "",
  } = data || {};

  const filterQueryString = Object.keys(filter).length
    ? new URLSearchParams(filter).toString()
    : "";

  const searchParams = new URLSearchParams();
  searchParams.set("limit", limit);
  searchParams.set("offset", offset);
  searchParams.set("status", status);
  searchParams.set("factory_code", factoryCode);
  searchParams.set("invoice_view", "true");
  searchParams.set("include", "credit_notes");

  if (search && searchBy && INVOICE_SEARCH_KEY_MAP[searchBy]) {
    searchParams.set(INVOICE_SEARCH_KEY_MAP[searchBy], search);
  }

  const filterStr = filterQueryString ? `&${filterQueryString}` : "";

  return api.get(
    `${BASE_URL}/invoice?${searchParams.toString()}${filterStr}`
  );
}

// No include=credit_notes needed — po-details already returns credit_notes inside each invoice object
export function getInvoiceListByPO(data) {
  const { po_code, include } = data;
  const includeParam = include ? `&include=${encodeURIComponent(include)}` : "";
  return api.get(`${BASE_URL}/po-details?po_code=${po_code}${includeParam}`);
}

export function uploadInvoice(data) {
  return api.post(`${BASE_URL}/bulk-invoice`, data.payload);
}

export function uploadPayments(data) {
  return api.post(`${BASE_URL}/payments`, data.payload);
}

export function addInvoice(data) {
  return api.post(`${BASE_URL}/invoice`, JSON.stringify(data.payload));
}

export function updateInvoice(data) {
  return api.put(`${BASE_URL}/invoice`, JSON.stringify(data.payload));
}

export function deleteInvoice(data) {
  return api.remove(
    `${BASE_URL}/invoice?invoice_no=${data.invoiceNo}&emp_id=${data.empId}&po_code=${data.po_code}`
  );
}

export async function getInvoicePresignedUrl(invoiceNo) {
  const params = new URLSearchParams();
  if (invoiceNo) params.set("invoice_no", invoiceNo);
  const res = await api.get(`${BASE_URL}/invoice?${params.toString()}`);
  return res?.invoice_list?.[0]?.invoice_image?.pre_signed_url || null;
}

export function uploadInvoiceImage(data) {
  return api.post(
    `${BASE_URL}/invoice-image?invoice_no=${data.invoiceNo}&po_code=${data.poCode}`,
    data.payload
  );
}

export function downloadPOData(data) {
  const { vendorName, fromDate, toDate, approver } = data || {};
  return api.get(
    `${BASE_URL}/accounting?vendor=${vendorName}&start_date=${fromDate}&end_date=${toDate}${
      approver ? `&approver=${approver}` : ""
    }`
  );
}

export function getVendors(data) {
  return api.get(`${BASE_URL}/vendors?key=${data.query}`);
}

export function getApprovers() {
  return api.get(`${BASE_URL}/l2approvers`);
}

// po_code goes in the query, not the path: RCS PO codes contain "/" and the
// /fulfilment/* gateway decodes %2F before VIP routes the request (ENG-28753).
export function getGRNs(data) {
  return api.get(`${BASE_URL}/grn-list?po_code=${encodeURIComponent(data.po_code)}`);
}

export function setInvoiceFinanceDetails(payload) {
  return api.post(
    `${BASE_URL}/invoice-finance-details`,
    JSON.stringify(payload)
  );
}

export function getInvoiceRevisionHistory(id) {
  return api.get(`${BASE_URL}/invoice-history?id=${id}`);
}

export async function approveBulkInvoice(data) {
  return api.put(`${BASE_URL}/approve-invoices`, JSON.stringify(data.payload));
}

export async function sendEmailReminder(data) {
  return api.post(`/fulfilment/order/api/v1/po/${data.po_id}/send_email`, JSON.stringify(data.payload));
}


export async function getPODoc(data) {
  const params = new URLSearchParams({ po_code: data.po_code });
  if (data.source) params.set("source", data.source);
  if (data.fins_status) params.set("fins_status", data.fins_status);
  return api.get(`${BASE_URL}/po-doc?${params.toString()}`);
}

export async function acknowledgePO(data) {
  return api.post(`${BASE_URL}/acknowledge-po`, JSON.stringify(data));
}

// Reads an uploaded credit note with VIP's CreditNoteParser (vip#421, ADR 0064) so the
// submission form can be pre-filled. Synchronous on the server (up to ~50s).
export function parseCreditNote({ attachmentId, poCode }) {
  return api.post(
    `${BASE_URL}/credit-note/parse`,
    JSON.stringify({ attachment_id: attachmentId, po_code: poCode }),
  );
}

export function getCreditNoteAttachments(creditNoteId) {
  return api.get(`/fulfilment/accounts-payable/api/v2/CREDIT_NOTE/${creditNoteId}/attachments`);
}

export function getCreditNoteDetails(creditNoteId) {
  return api.get(`${BASE_URL}/credit-note/${creditNoteId}`);
}

export function updateCreditNote(creditNoteId, payload) {
  return api.post(`${BASE_URL}/credit-note/${creditNoteId}`, JSON.stringify(payload));
}

export function getCities() {
  return api.get("/sales/crm/api/v1/cities");
}
